import { createSlice } from "@reduxjs/toolkit"
import { Agency, User } from "types/user"
import { destroyCookie, getCookie } from "utils/cookie";
import { fetchFirebaseBackendTokenThunk, fetchSocialAccountsThunk, fetchUser, login, register } from "./thunk";
import { persistReducer } from "redux-persist";
import storage from 'redux-persist/lib/storage';
import { Master } from "types";
import { UserCredential } from "firebase/auth";
import { SocialAccountData } from "types/social-account";

export interface AuthState {
    loading: boolean,
    error: string | null,
    user: User | null,
    token: string | null,
    isAuthenticated: boolean,
    selectedAgencyId: string | null,
    selectedAgency: Agency | null,
    privileges: string [];
    notification_preferences: string[],
    configurations: Record<string, any>,
    master: Master | null,
    firebase_token: null | string,
    firebase_tokens: Record<string, {
        token: string,
        generatedAt: number 
    }>,
    social_accounts: {
        items: SocialAccountData[],
        last_fetched_at: number,
        error: string | null
        loading: boolean,
    },
}

const initialState: AuthState = {
    user: null,
    loading: false,
    error: null,
    token: getCookie('agencyAuthToken'),
    isAuthenticated: !!getCookie('agencyAuthToken'),
    selectedAgencyId: null,
    selectedAgency: null,
    privileges: [],
    notification_preferences: [],
    configurations: {},
    master: null,
    firebase_token: null,
    firebase_tokens: {},
    social_accounts: {
        items: [],
        last_fetched_at: 0,
        error: null,
        loading: false,     
    }
}

const AuthSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setError: (state, action) => {
            state.error = action.payload;
        },
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            destroyCookie('agencyAuthToken');
        },
        resetAuthFlags: (state) => {
            state.error = null
            state.loading = false
        },
        setSelectedAgency: (state, action) => {
            state.selectedAgency = action.payload;
            state.selectedAgencyId = action.payload.uuid;
            state.privileges = Array.isArray(action.payload.privileges) ? action.payload.privileges : [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                state.error = null;
                state.loading = true
            })
            .addCase(login.fulfilled, (state, action) => {
                state.token = action.payload;
                state.isAuthenticated = true;
                state.error = null;
                state.loading = false;
            })
            .addCase(login.rejected, (state, action) => {
                state.error = action.payload || 'Login Failed!';
                state.loading = false;
            })
            .addCase(register.pending, (state) => {
                state.error = null;
                state.loading = true
            })
            .addCase(register.fulfilled, (state, action) => {
                state.token = action.payload;
                state.isAuthenticated = true;
                state.error = null;
                state.loading = false;
            })
            .addCase(register.rejected, (state, action) => {
                state.error = action.payload || 'Registration Failed!';
                state.loading = false;
            })
            .addCase(fetchUser.pending, (state) => {
                state.error = null;
                state.loading = true
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.user = action.payload.user

                const agencies = Array.isArray(action.payload.user.agencies) ? action.payload.user.agencies : [];
                const selectedAgencyId = state.selectedAgencyId ? state.selectedAgencyId :
                    (agencies.length ? action.payload.user.agencies[0].uuid : null);

                const selectedAgency = agencies.find((agency) => agency.uuid === selectedAgencyId) ?? (agencies[0] ?? null);
                state.selectedAgency = selectedAgency ?? null;
                state.selectedAgencyId = selectedAgency ? selectedAgency.uuid : null;
                state.privileges = selectedAgency ? selectedAgency.privileges : [];
                state.notification_preferences = selectedAgency?.notification_preferences ?? [];
                state.configurations = selectedAgency?.configurations ?? {};
                state.master = action.payload.master;

                state.error = null;
                state.loading = false;
            })
            .addCase(fetchUser.rejected, (state, action) => {
                state.error = action.payload || 'Failed to fetch user profile!';
                state.loading = false;
            })
            .addCase(fetchFirebaseBackendTokenThunk.fulfilled, (state, {payload}) => {
                state.firebase_tokens[payload.agencyId] = {
                    token: payload.token,
                    generatedAt: Date.now()
                };
            })
            .addCase(fetchSocialAccountsThunk.pending, (state, _) => {
                state.social_accounts.loading = true;
                state.social_accounts.error = null;
            })
            .addCase(fetchSocialAccountsThunk.fulfilled, (state, {payload}) => {
                state.social_accounts.items = payload.accounts;
                state.social_accounts.last_fetched_at = payload.fetchedAt;
                state.social_accounts.loading = false;
                state.social_accounts.error = null;
            })
            .addCase(fetchSocialAccountsThunk.rejected, (state, {payload}) => {
                state.social_accounts.loading = false;
                state.social_accounts.error = payload || 'Failed to fetch social accounts!';
            })
    }
});

export const { setError, logout, resetAuthFlags, setSelectedAgency } = AuthSlice.actions;

export default persistReducer({
    key: 'auth',
    storage,
    whitelist: ['selectedAgencyId', 'firebase_tokens']
}, AuthSlice.reducer)

